import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Sparkles, ExternalLink, Heart } from "lucide-react";
import { adsApi, adsApiCall } from "./api";
import "./ads-creator.css";

export default function PublicAchievement() {
  const { hash, achievementId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const ogUrl = `${adsApi.defaults.baseURL}/achievements/og/${hash}/${achievementId}.png`;
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/a/${hash}/${achievementId}` : "";

  useEffect(() => {
    adsApiCall.publicAchievement(hash, achievementId).then(setData).catch((e) => {
      setErr(e?.response?.status === 404 ? "Badge not found" : "Couldn't load badge");
    });
  }, [hash, achievementId]);

  if (err) {
    return (
      <div className="ads-root">
        <div className="ads-empty-state">
          <div style={{ marginBottom: 12 }}>{err}</div>
          <Link to="/ads-creator" className="ads-btn ads-btn-primary">Start earning your own badges</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ads-root"><div className="ads-empty-state"><Loader2 className="animate-spin" /> Loading…</div></div>
    );
  }

  return (
    <div className="ads-root" data-testid="public-achievement">
      <Helmet>
        <title>{`${data.label} · Achievement · TopRankPulse`}</title>
        <meta name="description" content={`${data.description} — unlocked by ${data.owner_initials} on TopRankPulse.`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${data.label} — ${data.tier.toUpperCase()} achievement unlocked`} />
        <meta property="og:description" content={data.description} />
        <meta property="og:image" content={ogUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogUrl} />
      </Helmet>

      <div className={`pa-hero pa-tier-${data.tier}`} data-testid="pa-hero">
        <div className="ads-container" style={{ textAlign: "center", padding: "64px 24px" }}>
          <div className={`pa-badge pa-tier-${data.tier}`}>
            <span className="pa-badge-glyph">★</span>
          </div>
          <div className="pa-eyebrow">ACHIEVEMENT · {data.tier.toUpperCase()}</div>
          <h1 className="ads-h1" style={{ fontSize: "clamp(36px, 5.4vw, 64px)", marginTop: 10 }}>{data.label}</h1>
          <p className="ads-lead" style={{ maxWidth: 560, margin: "14px auto 24px" }}>{data.description}</p>
          <div className="pa-unlocked-by" data-testid="pa-owner">
            Unlocked by <b>{data.owner_initials}</b>
            {data.unlocked_at && <> · {new Date(data.unlocked_at).toLocaleDateString()}</>}
          </div>
          <div className="ads-cta-row" style={{ justifyContent: "center", marginTop: 28 }}>
            <Link to="/ads-creator" className="ads-btn ads-btn-primary" data-testid="pa-cta-primary">
              <Sparkles size={16} /> Earn your own badges
            </Link>
            <Link to="/ads-creator/instant-campaign" className="ads-btn ads-btn-ghost" data-testid="pa-cta-secondary">
              <ExternalLink size={14} /> Try Instant Campaign
            </Link>
          </div>
        </div>
      </div>

      <div className="pc-brand-footer">
        <div className="ads-container pc-brand-row">
          <div className="pc-brand-mark">
            <Heart size={14} fill="currentColor" />
            <span>Created with <b>TopRankPulse</b></span>
          </div>
          <Link to="/ads-creator" className="ads-btn ads-btn-ghost" style={{ padding: "6px 12px", fontSize: 13 }}>
            Visit TopRankPulse <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
